import React, { useState } from 'react';
import { FONTS } from '../../styles/tokens';
import type { ScannedEnvVar } from '../../lib/deploy-api';

interface Colors {
  text: string;
  textSecondary: string;
  card: string;
  border: string;
}

interface SecretRowProps {
  entry: ScannedEnvVar;
  value: string;
  onChangeValue: (value: string) => void;
  onToggleSecret: (isSecret: boolean) => void;
  onSkip: () => void;
  skipped: boolean;
  colors: Colors;
}

/**
 * One row in the Secret Capture step — name + hint + value input +
 * secret/plain toggle + skip button. Renders inline help from the AI
 * classifier when available.
 */
export function SecretRow(props: SecretRowProps): React.JSX.Element {
  const [reveal, setReveal] = useState(false);
  const { entry, colors } = props;
  return (
    <div
      style={{
        padding: '12px', borderRadius: '8px',
        border: `1px solid ${colors.border}`, background: colors.card,
        opacity: props.skipped ? 0.55 : 1,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', marginBottom: '6px' }}>
        <div>
          <div style={{ fontFamily: FONTS.heading, fontSize: '13px', color: colors.text, letterSpacing: '0.05em' }}>
            {entry.name}
          </div>
          {entry.service && (
            <div style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary }}>
              {entry.service}
              {entry.description ? ` — ${entry.description}` : ''}
            </div>
          )}
        </div>
        <span
          style={{
            padding: '2px 8px', borderRadius: '10px',
            fontFamily: FONTS.heading, fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase',
            backgroundColor: entry.isSecret ? '#F7E6EC' : '#E6F4FA',
            color: entry.isSecret ? '#9B2C4A' : '#2D6A8A',
          }}
        >
          {entry.isSecret ? 'Secret' : 'Env'}
        </span>
      </div>

      <div style={{ fontFamily: FONTS.body, fontSize: '11px', color: colors.textSecondary, marginBottom: '8px' }}>
        Found in {entry.occurrences} {entry.occurrences === 1 ? 'reference' : 'references'}
        {entry.locations.length > 0 && ` · ${entry.locations.slice(0, 2).join(', ')}`}
      </div>

      <div style={{ display: 'flex', gap: '8px' }}>
        <input
          type={reveal || !entry.isSecret ? 'text' : 'password'}
          value={props.value}
          onChange={e => props.onChangeValue(e.target.value)}
          placeholder={entry.suggestedDefault ?? 'Enter value'}
          disabled={props.skipped}
          style={{
            flex: 1, padding: '8px 10px', borderRadius: '6px',
            border: `1px solid ${colors.border}`,
            backgroundColor: colors.card, color: colors.text,
            fontFamily: FONTS.body, fontSize: '13px',
          }}
        />
        {entry.isSecret && (
          <button
            type="button"
            onClick={() => setReveal(v => !v)}
            style={{ padding: '6px 10px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSecondary, cursor: 'pointer', fontFamily: FONTS.body, fontSize: '12px' }}
          >
            {reveal ? 'Hide' : 'Show'}
          </button>
        )}
        <button
          type="button"
          onClick={() => props.onToggleSecret(!entry.isSecret)}
          title={entry.isSecret ? 'Mark as non-secret (stored as plain config)' : 'Mark as secret (encrypted)'}
          style={{ padding: '6px 10px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSecondary, cursor: 'pointer', fontFamily: FONTS.body, fontSize: '12px' }}
        >
          ⇄
        </button>
        <button
          type="button"
          onClick={props.onSkip}
          style={{ padding: '6px 10px', borderRadius: '6px', border: `1px solid ${colors.border}`, background: 'transparent', color: colors.textSecondary, cursor: 'pointer', fontFamily: FONTS.body, fontSize: '12px' }}
        >
          {props.skipped ? 'Include' : 'Skip'}
        </button>
      </div>
    </div>
  );
}
